/**
 * Studio / test fixtures for clip-v2: one AAPL example per format.
 *
 * In production every number comes from the Praevue engine. Here a small
 * Black-Scholes model generates deterministic, internally consistent numbers
 * (premiums, curves, breakevens, P&L), and the hook copy is built from them.
 */
import { aaplLongCallScenarioProps } from "../../fixtures/aaplLongCallScenario.js";

const RATE = 0.04;
const CONTRACT = 100;

function normCdf(x) {
  const t = 1 / (1 + 0.2316419 * Math.abs(x));
  const d = 0.3989423 * Math.exp((-x * x) / 2);
  const p = d * t * (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))));
  return x > 0 ? 1 - p : p;
}

function bsCall(spot, strike, days, iv) {
  const years = days / 365;
  if (years <= 0) return Math.max(0, spot - strike);
  const sq = iv * Math.sqrt(years);
  const d1 = (Math.log(spot / strike) + (RATE + (iv * iv) / 2) * years) / sq;
  return spot * normCdf(d1) - strike * Math.exp(-RATE * years) * normCdf(d1 - sq);
}

const round2 = (v) => Math.round(v * 100) / 100;
const money = (v) => `$${Math.abs(Math.round(v)).toLocaleString("en-US")}`;

function grid(lo, hi, step) {
  const out = [];
  for (let p = lo; p <= hi + 1e-9; p += step) out.push(round2(p));
  return out;
}

const PRICES = grid(200, 262, 1);

/** P&L curve (dollars, 1 contract) of a set of call legs, valued `days` before expiration at `iv`. */
function curve(legs, cost, days, iv) {
  return PRICES.map((p) => ({
    underlyingPrice: p,
    pnl: round2((legs.reduce((sum, leg) => sum + leg.qty * bsCall(p, leg.strike, days, iv), 0) - cost) * CONTRACT),
  }));
}

const COMMON = {
  ticker: "AAPL",
  cta: { text: "Try it with your numbers", url: "praevue.com/s/demo01" },
  dataAsOf: "Oct 1, 2026 4:00 PM ET",
  disclaimer:
    "Options involve risk and are not suitable for all investors. This example is hypothetical and for illustration only.",
  brand: { name: "Praevue", logoUrl: null },
};

// ── why-it-lost: long call through an earnings IV crush ─────
const W = { strike: 230, from: 225, to: 231.75, dte: 30, days: 2, ivFrom: 0.45, ivTo: 0.26 };
const wPremium = round2(bsCall(W.from, W.strike, W.dte, W.ivFrom));
const wLegs = [{ strike: W.strike, qty: 1 }];
const wPnlAtMove = Math.round((bsCall(W.to, W.strike, W.dte - W.days, W.ivTo) - wPremium) * CONTRACT);
const wMovePct = Math.round(((W.to - W.from) / W.from) * 100);
const wIvChange = Math.round((W.ivTo - W.ivFrom) * 100);

export const whyItLostClip = {
  format: "why-it-lost",
  ...COMMON,
  spot: W.from,
  hooks: [
    `AAPL went **UP ${wMovePct}%**. This call still **LOST ${money(wPnlAtMove)}**.`,
    "The stock went up. Why did the call **LOSE** value?",
    `**+${wMovePct}%** on the stock. **−${money(wPnlAtMove)}** on the call.`,
  ],
  hookIndex: 0,
  strategy: {
    label: "Long Call",
    legs: `Buy 1 AAPL ${W.strike} call · ${W.dte} days · $${wPremium.toFixed(2)}`,
  },
  numbers: {
    maxLoss: round2(wPremium * CONTRACT),
    maxGain: "Unlimited",
    breakevens: [round2(W.strike + wPremium)],
    netDebitCredit: round2(wPremium * CONTRACT),
  },
  payoff: {
    expiration: curve(wLegs, wPremium, 0, W.ivTo),
    tPlus0: curve(wLegs, wPremium, W.dte - W.days, W.ivTo),
  },
  scenarioMove: { fromSpot: W.from, toSpot: W.to, days: W.days, ivChangePct: wIvChange },
  pnlAtMove: wPnlAtMove,
  lesson: { text: "Up isn’t enough when volatility collapses.", number: null },
};

// ── compare-two: long call vs bull call spread ──────────────
const C = { spot: 227.5, dte: 30, iv: 0.3 };
const cCall = round2(bsCall(C.spot, 230, C.dte, C.iv));
const cSpread = round2(bsCall(C.spot, 230, C.dte, C.iv) - bsCall(C.spot, 240, C.dte, C.iv));
const cCallLegs = [{ strike: 230, qty: 1 }];
const cSpreadLegs = [
  { strike: 230, qty: 1 },
  { strike: 240, qty: -1 },
];

export const compareTwoClip = {
  format: "compare-two",
  ...COMMON,
  spot: C.spot,
  hooks: [
    `Same AAPL view. One trade risks **${money((cCall - cSpread) * CONTRACT)} LESS**.`,
    "Bullish on AAPL? Compare the **DOWNSIDE** first.",
  ],
  hookIndex: 0,
  view: "Bullish on AAPL",
  strategies: [
    {
      label: "Long Call",
      legs: `Buy 230 call · $${cCall.toFixed(2)}`,
      numbers: {
        maxLoss: round2(cCall * CONTRACT),
        maxGain: "Unlimited",
        breakevens: [round2(230 + cCall)],
        netDebitCredit: round2(cCall * CONTRACT),
      },
      payoff: { expiration: curve(cCallLegs, cCall, 0, C.iv), tPlus0: null },
    },
    {
      label: "Call Spread",
      legs: `Buy 230 / sell 240 call · $${cSpread.toFixed(2)}`,
      numbers: {
        maxLoss: round2(cSpread * CONTRACT),
        maxGain: round2((10 - cSpread) * CONTRACT),
        breakevens: [round2(230 + cSpread)],
        netDebitCredit: round2(cSpread * CONTRACT),
      },
      payoff: { expiration: curve(cSpreadLegs, cSpread, 0, C.iv), tPlus0: null },
    },
  ],
  verdict: null,
  lesson: { text: "Cap the upside, shrink the downside.", number: null },
};

// ── change-one: same price, 23 days later (time decay) ──────
const T = { spot: 227.5, iv: 0.3, from: 30, to: 7 };
const tPremium = round2(bsCall(T.spot, 230, T.from, T.iv));
const tLegs = [{ strike: 230, qty: 1 }];
const tBaselinePnl = Math.round((bsCall(T.spot, 230, T.from, T.iv) - tPremium) * CONTRACT);
const tChangedPnl = Math.round((bsCall(T.spot, 230, T.to, T.iv) - tPremium) * CONTRACT);

export const changeOneClip = {
  format: "change-one",
  ...COMMON,
  spot: T.spot,
  hooks: [
    `Same stock price. **${T.from - T.to} days later** this call is **DOWN ${money(tChangedPnl - tBaselinePnl)}**.`,
    "Change **ONE** assumption and watch this call shrink.",
  ],
  hookIndex: 0,
  strategy: { label: "Long Call", legs: `Buy 1 AAPL 230 call · ${T.from} days · $${tPremium.toFixed(2)}` },
  numbers: {
    maxLoss: round2(tPremium * CONTRACT),
    maxGain: "Unlimited",
    breakevens: [round2(230 + tPremium)],
    netDebitCredit: round2(tPremium * CONTRACT),
  },
  assumption: { kind: "time", from: T.from, to: T.to, label: null },
  baseline: { payoff: curve(tLegs, tPremium, T.from, T.iv), pnl: tBaselinePnl },
  changed: { payoff: curve(tLegs, tPremium, T.to, T.iv), pnl: tChangedPnl },
  lesson: { text: "Time is a cost you pay daily.", number: null },
};

// ── explainer: v1 content on the v2 motion system ───────────
const v1 = aaplLongCallScenarioProps;
export const explainerClip = {
  format: "explainer",
  ...COMMON,
  spot: v1.spot,
  hooks: ["The stock went up. Why did the call **LOSE** value?"],
  hookIndex: 0,
  strategy: { label: v1.strategyLabel, legs: v1.legsSummary.replace(/, /g, " · ") },
  numbers: v1.numbers,
  payoff: v1.payoff,
  beats: v1.beats,
  lesson: { text: "Volatility matters as much as direction.", number: v1.beats[2].emphasis },
};

export const CLIP_V2_FIXTURES = {
  "why-it-lost": whyItLostClip,
  "compare-two": compareTwoClip,
  "change-one": changeOneClip,
  explainer: explainerClip,
};

/** Composition defaultProps shape: the clip lives under `clip`. */
export const clipV2DefaultInputProps = { clip: whyItLostClip };
